import { chmod, mkdir, writeFile } from "node:fs/promises";

/**
 * Owner-only writes for files holding key material (#622).
 *
 * WHY THIS EXISTS: `writeFile(path, data, { mode })` applies `mode` only when
 * the file is CREATED. Writing over an existing file leaves its permissions
 * exactly as they were. Every write site in this package already passed
 * `mode: 0o600` and every one of them was a no-op for files that predated it —
 * so an install created before the mode was introduced keeps world-readable
 * credentials forever, while the code reads as though it is protected.
 *
 * Measured on a live agent (loopita, 2026-08-04):
 *
 *     -rw-r--r--  agentvault.json        <- created early, never tightened
 *     -rw-------  agentvault.json.bak    <- created later, born correct
 *     -rw-------  mls-*.json  (x12)
 *
 * Same file, same writer, different modes — decided entirely by which version
 * of the code happened to create it first.
 *
 * `mkdir(..., { mode })` has the same create-only behaviour AND is masked by
 * umask, so directories get the same unconditional treatment.
 * `claude-room-bridge/src/host-trust.ts` already does this ("chmod
 * unconditionally; both paths are ours") — this brings the plugin in line.
 *
 * SCOPE: this narrows exposure to OTHER uids. It does NOT defend against the
 * agent itself, which runs as the SAME uid as the bridge and can read anything
 * the bridge can. That is the substance of #622 and needs a process/uid
 * boundary, not a permission bit. This is the floor, not the fix.
 */
export const DIR_MODE = 0o700;
export const FILE_MODE = 0o600;

/** Create the directory if needed and enforce 0700 even if it already existed. */
export async function ensureSecureDir(dir: string): Promise<void> {
  await mkdir(dir, { recursive: true, mode: DIR_MODE });
  try {
    await chmod(dir, DIR_MODE);
  } catch {
    // Not ours to chmod (unusual ownership) — the write below still succeeds or
    // fails on its own merits. Never block persistence on a permission tidy-up.
  }
}

/**
 * Write a file and enforce 0600 whether or not it already existed.
 *
 * `flag` is passed through for the exclusive-create lock files in mls-state.
 */
export async function writeSecureFile(
  filePath: string,
  data: string,
  flag?: string,
): Promise<void> {
  await writeFile(filePath, data, { encoding: "utf-8", mode: FILE_MODE, ...(flag ? { flag } : {}) });
  try {
    await chmod(filePath, FILE_MODE);
  } catch {
    // The bytes are already on disk; a failed tidy-up must not look like a
    // failed save. Callers treat a throw here as "state was not persisted".
  }
}
